<template>
  <div
    ref="rootRef"
    class="flex w-full scroll-mt-[48px] flex-col gap-3 sm:flex-row sm:items-center sm:gap-3 lg:scroll-mt-[64px]"
  >
    <label class="sr-only" :for="inputId">{{ resolvedPlaceholder }}</label>
    <input
      :id="inputId"
      ref="inputRef"
      v-model="query"
      type="text"
      enterkeyhint="search"
      autocomplete="off"
      :placeholder="resolvedPlaceholder"
      class="box-border h-12 min-h-12 w-full flex-1 rounded-lg border border-neutral-200 bg-white px-3 text-[16px] leading-none text-neutral-900 shadow-[0px_1px_1px_rgba(16,24,40,0.04)] placeholder-neutral-500 outline-none transition-colors focus:border-neutral-400 focus:ring-1 focus:ring-neutral-300"
      @input="onInput"
      @keydown.enter.prevent="onSearchClick"
    >

    <button
      type="button"
      class="btn-nav-outline box-border inline-flex h-12 min-h-12 w-full shrink-0 items-center justify-center gap-2 rounded-lg text-[16px] font-semibold leading-none sm:w-auto"
      @click="onSearchClick"
    >
      <Icon name="search" class="size-5 shrink-0" aria-hidden="true" />
      {{ resolvedButtonLabel }}
    </button>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import Icon from '@/Shared/Icon.vue';
import { useTranslations } from '@/utils/translations.js';
import {
  afterLayoutSettle,
  getElementScrollTop,
  smoothScrollToElement,
} from '@/utils/scrollToAnchor';
import {
  mainHeaderPinningActive,
  mainHeaderRevealHeight,
  mainHeaderRevealed,
} from '@/utils/stickyMainHeader';

const { t } = useTranslations();

const props = defineProps({
  endpoint: {
    type: String,
    required: true,
  },
  idsKey: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  buttonLabel: {
    type: String,
    default: '',
  },
  // Extra query params sent with every search request (e.g. category).
  params: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:matchingIds']);

const rootRef = ref(null);
const inputRef = ref(null);
const query = ref('');
const inputId = `index-search-${Math.random().toString(36).slice(2, 9)}`;

const resolvedPlaceholder = computed(() => props.placeholder || t('search.placeholder'));
const resolvedButtonLabel = computed(() => props.buttonLabel || t('search.button'));

let debounceTimer = null;
let requestToken = 0;

function onInput() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runSearch, 200);
}

function onSearchClick() {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  runSearch();

  // Close mobile keyboard so viewport height settles before measuring scroll.
  inputRef.value?.blur();
  afterLayoutSettle(scrollSearchIntoView);
}

function scrollSearchIntoView() {
  const el = rootRef.value;
  if (!el || typeof window === 'undefined') return;

  const baseTop = getElementScrollTop(el);
  const scrollingUp = baseTop < window.scrollY - 1;
  const headerOffset = mainHeaderPinningActive.value ? mainHeaderRevealHeight.value : 0;
  const extraOffset =
    mainHeaderRevealed.value || scrollingUp
      ? Math.round(headerOffset / 2)
      : 0;

  smoothScrollToElement(el, { extraOffset });
}

async function runSearch() {
  const term = query.value.trim();
  const token = ++requestToken;

  // Empty query clears the server-side filter and shows the full list again.
  if (!term) {
    emit('update:matchingIds', null);
    return;
  }

  try {
    const params = new URLSearchParams({ q: term });
    Object.entries(props.params || {}).forEach(([key, value]) => {
      if (value != null && value !== '') params.set(key, String(value));
    });

    const response = await fetch(`${props.endpoint}?${params}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    if (token !== requestToken) return;

    const ids = Array.isArray(payload[props.idsKey]) ? payload[props.idsKey] : [];
    emit('update:matchingIds', ids);
  } catch {
    if (token !== requestToken) return;
    emit('update:matchingIds', []);
  }
}

watch(() => props.params, () => {
  if (query.value.trim()) runSearch();
}, { deep: true });

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>
